import { CreateEventDto, EventTypeBackend, EventUnion } from '../../../types/events.types';
import { toBackendDateFormat } from '../../../utils/to-backend-date-format.util';

/**
 * Deleting a single transaction is an event edit that resends every remaining line item, so the
 * source event must carry the item relations (stakeholder, share class, plan). Build this from the
 * event detail response — the timeline list loads items without those relations.
 *
 * Every item field the backend persists must be resent — anything left out is cleared by the
 * recreate (a dropped `reverseVesting` wiped the schedules of every remaining row).
 *
 * Only the *items* carry an `id` (the backend reuses it to preserve signatures across the recreate).
 * The sub-event object must not: every `create*EventSchema` is `.strict()`, so a stray top-level
 * `id` fails validation with `Unrecognized key: "id"` before the edit workflow ever runs.
 */
export const buildTransactionDeletePayload = (
  event: EventUnion,
  transactionId: string,
): CreateEventDto => {
  const { type } = event;

  const buyback = type === EventTypeBackend.BUYBACK && {
    items: event.items
      .filter((item) => item.id !== transactionId)
      .map(({ date, shareClass, stakeholder, sharesCount, id, filesLinks }) => ({
        date: toBackendDateFormat(date),
        stakeholderId: stakeholder.id,
        shareClassId: shareClass.id,
        sharesCount,
        id,
        filesLinks,
      })),
  };

  const conversion = type === EventTypeBackend.CONVERSION && {
    items: event.items
      .filter((item) => item.id !== transactionId)
      .map(({ date, fromShareClass, sharesCount, toShareClass, stakeholder, id }) => ({
        date: toBackendDateFormat(date),
        stakeholderId: stakeholder.id,
        fromShareClassId: fromShareClass.id,
        toShareClassId: toShareClass.id,
        sharesCount,
        id,
      })),
  };

  const fundraisingRound = type === EventTypeBackend.FUNDRAISING_ROUND && {
    valuationId: event.valuation.id,
    sharePrice: event.sharePrice,
    roundingSetting: event.roundingSetting,
    isOpen: event.isOpen,
    date: toBackendDateFormat(event.date),
    items: event.items
      .filter((item) => item.id !== transactionId)
      .map(({ date, investedValue, shareClass, stakeholder, id, filesLinks }) => ({
        id,
        stakeholderId: stakeholder.id,
        shareClassId: shareClass.id,
        investedValue,
        date: toBackendDateFormat(date),
        filesLinks,
      })),
  };

  const grant = type === EventTypeBackend.GRANT && {
    items: event.items
      .filter((item) => item.id !== transactionId)
      .map(({ id, date, numbersOfGrants, plan, stakeholder, filesLinks }) => ({
        id,
        date: toBackendDateFormat(date),
        planId: plan.id,
        stakeholderId: stakeholder.id,
        numbersOfGrants,
        filesLinks,
      })),
  };

  const secondaries = type === EventTypeBackend.SECONDARIES && {
    items: event.items
      .filter((item) => item.id !== transactionId)
      .map(
        ({
          date,
          shareClass,
          fromStakeholder,
          sharesCount,
          sharesValue,
          toStakeholder,
          id,
          filesLinks,
        }) => ({
          date: toBackendDateFormat(date),
          fromStakeholderId: fromStakeholder.id,
          toStakeholderId: toStakeholder.id,
          shareClassId: shareClass.id,
          sharesCount,
          sharesValue,
          id,
          filesLinks,
        }),
      ),
  };

  const issuance = type === EventTypeBackend.SHARE_ISSUANCE && {
    valuationId: event.valuation.id,
    date: toBackendDateFormat(event.date),
    sharePrice: event.sharePrice,
    items: event.items
      .filter((item) => item.id !== transactionId)
      .map(({ date, sharesCount, shareClass, stakeholder, id, filesLinks, reverseVesting }) => ({
        stakeholderId: stakeholder.id,
        shareClassId: shareClass.id,
        sharesCount,
        date: toBackendDateFormat(date),
        id,
        filesLinks,
        // The edit recreates every row from the payload, so an omitted schedule is a removal. The
        // detail response carries `null` for rows without one, which the `.optional()` field rejects.
        reverseVesting: reverseVesting ?? undefined,
      })),
  };

  return {
    type,
    grant: grant || undefined,
    fundraisingRound: fundraisingRound || undefined,
    issuance: issuance || undefined,
    buyback: buyback || undefined,
    secondaries: secondaries || undefined,
    conversion: conversion || undefined,
    valuation: undefined,
    filesLinks: event.filesLinks,
    additionalNotes: event.additionalNotes,
  };
};
